import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { categoriesApi } from "@/lib/admin-api/categories";
import type { CategoryFormData, ReorderItem } from "@/lib/admin-api/categories";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const CATEGORY_KEYS = {
  all: ["admin", "categories"] as const,
  tree: () => [...CATEGORY_KEYS.all, "tree"] as const,
} as const;

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Fetch the full nested category tree. */
export function useCategoryTree() {
  return useQuery({
    queryKey: CATEGORY_KEYS.tree(),
    queryFn: () => categoriesApi.getTree(),
    staleTime: 30_000,
    select: (data) => data.categories,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** Create a new category, then invalidate the tree. */
export function useCreateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CategoryFormData) => categoriesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATEGORY_KEYS.all });
    },
  });
}

/** Update an existing category, then invalidate the tree. */
export function useUpdateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      readonly id: string;
      readonly data: Partial<CategoryFormData>;
    }) => categoriesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATEGORY_KEYS.all });
    },
  });
}

/** Delete a category, then invalidate the tree. */
export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      moveChildrenTo,
    }: {
      readonly id: string;
      readonly moveChildrenTo?: string | null;
    }) => categoriesApi.delete(id, moveChildrenTo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATEGORY_KEYS.all });
    },
  });
}

/** Bulk reorder categories within the same level. */
export function useReorderCategories() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (items: readonly ReorderItem[]) =>
      categoriesApi.reorder(items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATEGORY_KEYS.all });
    },
  });
}
